import {
  Box,
  Image,
  Text,
  VStack,
  IconButton,
  HStack,
} from '@chakra-ui/react';
import { CloseIcon, DownloadIcon } from '@chakra-ui/icons';
import { useState } from 'react';
import { formatJiraDate } from 'utils/formatJiraDate';
import { getFileType } from 'utils/attachmentPreview.utils';
import { FilePdfOutlined, FileOutlined } from '@ant-design/icons';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import Cookies from 'js-cookie';
import { DATA_API_CONSTANT } from 'services/constant/api.constant';

const AttachmentList = ({ attachments = [], onDelete }) => {
  const [previewAttachment, setPreviewAttachment] = useState(null);

  if (!attachments.length) return null;

  const handleDownload = async (attachmentId, fileName) => {
    if (!attachmentId) return;

    try {
      const tokenid = Cookies.get('authUser');
      const apiUrl = `${process.env.REACT_APP_API_URL}${DATA_API_CONSTANT.DOWNLOAD_ATTACHMENT}/${attachmentId}`;

      // Fetch the file with authentication
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          tokenid: tokenid || '',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob data
      const blob = await response.blob();

      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'download';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Clean up after a delay
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(blobUrl);
      }, 200);
    } catch (error) {
      console.error('Download failed:', error);
      // You might want to show a toast/notification here
    }
  };  
  
  return (
    <Box>
      {/* Header */}
      <HStack spacing={2} mb={2} display="flex" alignItems="center">
        <Text fontWeight="bold" mb={0}>Attachments</Text>
        <Box
          bg="gray.200"
          fontSize="xs"
          fontWeight="semibold"
          px={2}
          py="2px"
          borderRadius="md"
        >
          {attachments.length}
        </Box>
      </HStack>

      {/* List */}
      <VStack align="start" spacing={3}>
        {attachments.map((file) => {
          const type = getFileType(file.file_name);

          return (
            <Box
              key={file.attachment_id}
              position="relative"
              border="1px solid #e5e7eb"
              borderRadius="md"
              p={3}
              w="220px"
              bg="white"
              role="group"
              _hover={{ boxShadow: 'md', cursor: 'pointer' }}
              onClick={() => setPreviewAttachment(file)}
            >
              {/* Actions */}
              <HStack
                position="absolute"
                top="6px"
                right="6px"
                spacing={1}
                opacity={0}
                _groupHover={{ opacity: 1 }}
                zIndex={2}
              >
                <IconButton
                  icon={<DownloadIcon />}
                  size="xs"
                  aria-label="Download"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(file.attachment_id, file.file_name);
                  }}
                />

                <IconButton
                  icon={<CloseIcon />}
                  size="xs"
                  aria-label="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(file.attachment_id);
                  }}
                />
              </HStack>

              {/* Thumbnail */}
              {type === 'image' ? (
                <Image
                  src={file.file_url}
                  alt={file.file_name}
                  borderRadius="md"
                  mb={2}
                  maxH="120px"
                  objectFit="cover"
                />
              ) : type === 'pdf' ? (
                <FilePdfOutlined style={{ fontSize: 48, color: '#E53E3E' }} />
              ) : (
                <FileOutlined style={{ fontSize: 48 }} />
              )}

              <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                {file.file_name}
              </Text>

              <Text fontSize="xs" color="gray.500">
                {formatJiraDate(file.created_at)}
              </Text>
            </Box>
          );
        })}
      </VStack>

      {/* PREVIEW MODAL */}
      <AttachmentPreviewModal
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />
    </Box>
  );
};

export default AttachmentList;